import { DataType, type ModuleDefinition } from "@/lib/types/module"

export const MODULE_REGISTRY: ModuleDefinition[] = [
  // ─── Input ──────────────────────────────────────────────
  {
    module: "text_input",
    title: "Text Input",
    category: "input",
    description: "Provide text content as input to the pipeline",
    icon: "TextAlignLeft01Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "text",
        type: DataType.String,
        required: true,
        handle: false,
        description: "The text content",
        config: { default: "", multiline: true },
      },
    ],
    outputs: [
      {
        name: "text",
        type: DataType.String,
        handle: true,
        description: "The text output",
      },
    ],
  },
  {
    module: "file_input",
    title: "File Input",
    category: "input",
    description: "Upload a file as input to the pipeline",
    icon: "Attachment01Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "file",
        type: DataType.File,
        required: true,
        handle: false,
        description: "The file to upload",
      },
    ],
    outputs: [
      {
        name: "file",
        type: DataType.File,
        handle: true,
        description: "The uploaded file",
      },
    ],
  },
  {
    module: "number_input",
    title: "Number Input",
    category: "input",
    description: "Provide a numeric value as input to the pipeline",
    icon: "InputNumericIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "value",
        type: DataType.Number,
        required: true,
        handle: false,
        description: "The numeric value",
        config: { default: 0 },
      },
    ],
    outputs: [
      {
        name: "value",
        type: DataType.Number,
        handle: true,
        description: "The numeric output",
      },
    ],
  },
  {
    module: "boolean_input",
    title: "Boolean Input",
    category: "input",
    description: "Provide a true/false value as input to the pipeline",
    icon: "ToggleOnIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "value",
        type: DataType.Boolean,
        required: true,
        handle: false,
        description: "The boolean value",
        config: { default: false },
      },
    ],
    outputs: [
      {
        name: "value",
        type: DataType.Boolean,
        handle: true,
        description: "The boolean output",
      },
    ],
  },
  {
    module: "image_input",
    title: "Image Input",
    category: "input",
    description: "Upload an image as input to the pipeline",
    icon: "ImageUploadIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: false,
        description: "The image to upload",
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "The uploaded image",
      },
    ],
  },
  {
    module: "text_builder",
    title: "Text Builder",
    category: "input",
    description: "Build text from a template with {{variable}} placeholders filled by connected inputs",
    icon: "ThirdBracketIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "template",
        type: DataType.String,
        required: true,
        handle: false,
        description: "Template with {{variable}} placeholders",
        config: { default: "", multiline: true },
      },
      // Dynamic handle inputs are derived at runtime from the template
      // via getTextBuilderInputs() — not listed here
    ],
    outputs: [
      {
        name: "text",
        type: DataType.String,
        handle: true,
        description: "Assembled text with variables substituted",
      },
    ],
  },

  // ─── Generation ─────────────────────────────────────────
  {
    module: "llm_text",
    title: "LLM Text",
    category: "generation",
    description: "Generate text using a large language model",
    icon: "AiChat02Icon",
    supportedProviders: ["anthropic", "openai", "google"],
    modelConfig: { categories: ["text"] },
    inputs: [
      {
        name: "prompt",
        type: DataType.String,
        required: false,
        handle: true,
        description: "Input prompt — overrides the prompt model parameter when connected",
      },
    ],
    outputs: [
      {
        name: "text",
        type: DataType.String,
        handle: true,
        description: "Generated text output",
      },
    ],
  },
  {
    module: "image_gen",
    title: "Image Generation",
    category: "generation",
    description: "Generate images from text prompts",
    icon: "Image01Icon",
    supportedProviders: ["google"],
    modelConfig: { categories: ["image"] },
    inputs: [
      {
        name: "prompt",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text prompt describing the image to generate",
      },
      {
        name: "image",
        type: DataType.Image,
        required: false,
        handle: true,
        description: "Optional reference image for style or subject guidance",
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Generated image",
      },
    ],
  },
  {
    module: "video_gen",
    title: "Video Generation",
    category: "generation",
    description: "Generate video from an image and prompt",
    icon: "Video01Icon",
    supportedProviders: ["google", "replicate"],
    modelConfig: { categories: ["video"] },
    inputs: [
      {
        name: "prompt",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text prompt describing the video to generate",
      },
      {
        name: "image",
        type: DataType.Image,
        required: false,
        handle: true,
        description: "Optional reference image for image-to-video generation",
      },
    ],
    outputs: [
      {
        name: "video",
        type: DataType.Video,
        handle: true,
        description: "Generated video clip",
      },
    ],
  },
  {
    module: "audio_tts",
    title: "Text-to-Speech",
    category: "generation",
    description: "Convert text to spoken audio",
    icon: "VoiceIcon",
    supportedProviders: ["openai", "elevenlabs"],
    modelConfig: { categories: ["audio"] },
    inputs: [
      {
        name: "text",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text to convert to speech",
      },
    ],
    outputs: [
      {
        name: "audio",
        type: DataType.Audio,
        handle: true,
        description: "Generated speech audio",
      },
    ],
  },
  {
    module: "audio_music",
    title: "Music Generation",
    category: "generation",
    description: "Generate music from a text prompt",
    icon: "MusicNote01Icon",
    supportedProviders: ["replicate"],
    modelConfig: { categories: ["music"] },
    inputs: [
      {
        name: "prompt",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text prompt describing the music to generate",
      },
    ],
    outputs: [
      {
        name: "audio",
        type: DataType.Audio,
        handle: true,
        description: "Generated music audio",
      },
    ],
  },

  // ─── Transform ──────────────────────────────────────────
  {
    module: "background_remover",
    title: "Background Remover",
    category: "transform",
    description: "Remove the background from an image",
    icon: "Eraser01Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to process",
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Image with background removed",
      },
    ],
  },
  {
    module: "image_upscaler",
    title: "Image Upscaler",
    category: "transform",
    description: "Upscale an image to higher resolution",
    icon: "ZoomInAreaIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to upscale",
      },
      {
        name: "scale",
        type: DataType.String,
        required: false,
        handle: true,
        description: "Upscale factor",
        config: { default: "2x", choices: ["2x", "4x"] },
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Upscaled image",
      },
    ],
  },
  {
    module: "image_converter",
    title: "Image Converter",
    category: "transform",
    description: "Convert an image to a different format",
    icon: "ArrowDataTransferHorizontalIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to convert",
      },
      {
        name: "format",
        type: DataType.String,
        required: true,
        handle: false,
        description: "Target format",
        config: { default: "webp", choices: ["png", "jpg", "webp", "gif", "svg"] },
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Converted image",
      },
    ],
  },
  {
    module: "image_cropper",
    title: "Image Cropper",
    category: "transform",
    description: "Crop an image by coordinates or aspect ratio",
    icon: "ImageCropIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to crop",
      },
      {
        name: "mode",
        type: DataType.String,
        required: false,
        handle: false,
        description: "Crop mode",
        config: { default: "aspect_ratio", choices: ["aspect_ratio", "coordinates"] },
      },
      {
        name: "aspect_ratio",
        type: DataType.String,
        required: false,
        handle: false,
        description: "Aspect ratio preset (used when mode is aspect_ratio)",
        config: { default: "1:1", choices: ["1:1", "16:9", "4:3", "9:16", "3:2"] },
      },
      {
        name: "x",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Left offset in pixels (used when mode is coordinates)",
        config: { default: 0 },
      },
      {
        name: "y",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Top offset in pixels (used when mode is coordinates)",
        config: { default: 0 },
      },
      {
        name: "width",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Crop width in pixels (used when mode is coordinates)",
        config: { default: 512 },
      },
      {
        name: "height",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Crop height in pixels (used when mode is coordinates)",
        config: { default: 512 },
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Cropped image",
      },
    ],
  },
  {
    module: "image_resizer",
    title: "Image Resizer",
    category: "transform",
    description: "Resize an image to specific dimensions",
    icon: "Resize02Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to resize",
      },
      {
        name: "width",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Target width in pixels",
        config: { default: 1024 },
      },
      {
        name: "height",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Target height in pixels",
        config: { default: 1024 },
      },
    ],
    outputs: [
      {
        name: "image",
        type: DataType.Image,
        handle: true,
        description: "Resized image",
      },
    ],
  },
  {
    module: "text_formatter",
    title: "Text Formatter",
    category: "transform",
    description: "Transform text: change case, trim whitespace, or truncate",
    icon: "TextCheckIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "text",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text to format",
      },
      {
        name: "operation",
        type: DataType.String,
        required: true,
        handle: false,
        description: "Transformation to apply",
        config: { default: "trim", choices: ["trim", "uppercase", "lowercase", "truncate"] },
      },
      {
        name: "max_length",
        type: DataType.Number,
        required: false,
        handle: false,
        description: "Max characters (only used when operation is truncate)",
        config: { default: 280 },
      },
    ],
    outputs: [
      {
        name: "text",
        type: DataType.String,
        handle: true,
        description: "Formatted text",
      },
    ],
  },

  // ─── Agent ──────────────────────────────────────────────
  {
    module: "agent_node",
    title: "Agent",
    category: "agent",
    description: "Autonomous agent that reasons, uses tools, and iterates to complete a goal",
    icon: "AiBrain01Icon",
    supportedProviders: ["anthropic", "openai", "google"],
    modelConfig: { categories: ["text"] },
    inputs: [
      {
        name: "prompt",
        type: DataType.String,
        required: false,
        handle: true,
        description: "Task or goal for the agent to complete",
      },
    ],
    outputs: [
      {
        name: "text",
        type: DataType.String,
        handle: true,
        description: "Agent's final response",
      },
    ],
  },

  // ─── Control ────────────────────────────────────────────
  {
    module: "approval_gate",
    title: "Approval Gate",
    category: "control",
    description: "Pause the pipeline and wait for human approval before continuing",
    icon: "CheckmarkCircle01Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "trigger_in",
        type: DataType.Trigger,
        required: false,
        handle: true,
        description: "Control-flow trigger (ordering only, carries no data)",
      },
    ],
    outputs: [
      {
        name: "trigger_out",
        type: DataType.Trigger,
        handle: true,
        description: "Control-flow trigger passed downstream after approval",
      },
    ],
  },

  // ─── Output ─────────────────────────────────────────────
  {
    module: "video_assembly",
    title: "Video Assembly",
    category: "output",
    description: "Assemble video with audio and text overlay",
    icon: "VideoReplayIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "video",
        type: DataType.Video,
        required: true,
        handle: true,
        description: "Video track",
      },
      {
        name: "audio",
        type: DataType.Audio,
        required: false,
        handle: true,
        description: "Audio track to overlay",
      },
      {
        name: "overlay_text",
        type: DataType.String,
        required: false,
        handle: true,
        description: "Text to overlay on the video",
      },
      {
        name: "format",
        type: DataType.String,
        required: false,
        handle: true,
        description: "Output video format",
        config: { default: "mp4", choices: ["mp4", "webm", "mov"] },
      },
    ],
    outputs: [
      {
        name: "file",
        type: DataType.File,
        handle: true,
        description: "Assembled video file",
      },
    ],
  },
  {
    module: "text_output",
    title: "Text Output",
    category: "output",
    description: "Display text results from the pipeline",
    icon: "TextAlignLeftIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "text",
        type: DataType.String,
        required: true,
        handle: true,
        description: "Text to display",
      },
    ],
    outputs: [],
  },
  {
    module: "image_output",
    title: "Image Output",
    category: "output",
    description: "Display image results from the pipeline",
    icon: "Image01Icon",
    supportedProviders: [],
    inputs: [
      {
        name: "image",
        type: DataType.Image,
        required: true,
        handle: true,
        description: "Image to display",
      },
    ],
    outputs: [],
  },
  {
    module: "audio_output",
    title: "Audio Output",
    category: "output",
    description: "Play audio results from the pipeline",
    icon: "HeadphonesIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "audio",
        type: DataType.Audio,
        required: true,
        handle: true,
        description: "Audio to play",
      },
    ],
    outputs: [],
  },
  {
    module: "number_output",
    title: "Number Output",
    category: "output",
    description: "Display a numeric result from the pipeline",
    icon: "ChartBarLineIcon",
    supportedProviders: [],
    inputs: [
      {
        name: "value",
        type: DataType.Number,
        required: true,
        handle: true,
        description: "Number to display",
      },
    ],
    outputs: [],
  },
  {
    module: "compositor",
    title: "Compositor",
    category: "output",
    description: "Composite image, video, and text layers into a single output",
    icon: "Layers01Icon",
    supportedProviders: [],
    inputs: [
      // Dynamic -- derived at runtime from config.layers via getCompositorInputs()
    ],
    outputs: [
      {
        name: "media",
        type: DataType.Image,
        handle: true,
        description: "Composited output",
      },
    ],
  },
]

export function getModuleDefinition(moduleId: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => m.module === moduleId)
}

export function getModulesByCategory(category: ModuleDefinition["category"]): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => m.category === category)
}
